import { useState, useEffect } from 'react';
import { priceSubmissionService, LeaderboardUser } from '../services/api';
import { useTranslation, Trans } from 'react-i18next';

const LeaderboardPage = () => {
  const { t } = useTranslation();
  const [leaderboard, setLeaderboard] = useState<LeaderboardUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchLeaderboard();
  }, []);

  const fetchLeaderboard = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await priceSubmissionService.getLeaderboard(50);
      setLeaderboard(response.data.data);
    } catch (error: any) {
      console.error('Failed to fetch leaderboard:', error);
      setError(t('leaderboard.error'));
    } finally {
      setLoading(false);
    }
  };

  const getRankMedal = (rank: number) => {
    switch (rank) {
      case 1:
        return '🥇';
      case 2:
        return '🥈';
      case 3:
        return '🥉';
      default:
        return null;
    }
  };

  const getRankColor = (rank: number) => {
    switch (rank) {
      case 1:
        return 'bg-gradient-to-r from-yellow-100 to-yellow-50 border-yellow-300';
      case 2:
        return 'bg-gradient-to-r from-gray-100 to-gray-50 border-gray-300';
      case 3:
        return 'bg-gradient-to-r from-orange-100 to-orange-50 border-orange-300';
      default:
        return 'bg-white border-gray-200';
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <p className="mt-4 text-gray-600">{t('leaderboard.loading')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
          <p className="text-red-600">{error}</p>
          <button
            onClick={fetchLeaderboard}
            className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition"
          >
            {t('leaderboard.tryAgain')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">{t('leaderboard.title')}</h1>
          <p className="text-gray-600">
            {t('leaderboard.subtitle')}
          </p>
        </div>

        {/* Info Banner */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <div className="flex items-start gap-3">
            <span className="text-2xl">ℹ️</span>
            <div>
              <h3 className="font-semibold text-blue-900 mb-1">{t('leaderboard.info.title')}</h3>
              <p className="text-sm text-blue-800">
                <Trans i18nKey="leaderboard.info.desc" components={{ strong: <strong /> }} />
              </p>
              <p className="text-sm text-blue-800 mt-2">
                <Trans i18nKey="leaderboard.info.rewards" components={{ strong: <strong /> }} />
              </p>
            </div>
          </div>
        </div>

        {/* Leaderboard Table */}
        {leaderboard.length === 0 ? (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-12 text-center">
            <p className="text-gray-600 text-lg">{t('leaderboard.empty.title')}</p>
            <p className="text-sm text-gray-500 mt-2">{t('leaderboard.empty.desc')}</p>
          </div>
        ) : (
          <div className="space-y-3">
            {leaderboard.map((user, index) => {
              const rank = index + 1;
              const medal = getRankMedal(rank);
              const rankColor = getRankColor(rank);

              return (
                <div
                  key={user.id}
                  className={`${rankColor} border rounded-lg p-4 transition-all hover:shadow-md`}
                >
                  <div className="flex items-center gap-4">
                    {/* Rank */}
                    <div className="flex-shrink-0 w-12 text-center">
                      {medal ? (
                        <span className="text-3xl">{medal}</span>
                      ) : (
                        <span className="text-xl font-bold text-gray-500">#{rank}</span>
                      )}
                    </div>

                    {/* Profile Picture */}
                    <div className="flex-shrink-0">
                      {user.profilePicture ? (
                        <img
                          src={user.profilePicture}
                          alt={user.displayName}
                          className="w-12 h-12 rounded-full border-2 border-gray-300"
                        />
                      ) : (
                        <div className="w-12 h-12 rounded-full bg-gray-300 flex items-center justify-center">
                          <span className="text-gray-600 text-lg font-semibold">
                            {user.displayName.charAt(0).toUpperCase()}
                          </span>
                        </div>
                      )}
                    </div>

                    {/* User Info */}
                    <div className="flex-1 min-w-0">
                      <h3 className="text-lg font-semibold text-gray-900 truncate">
                        {user.displayName}
                      </h3>
                      <p className="text-sm text-gray-600">
                        {user.approvedSubmissions} {t('leaderboard.submission', { count: user.approvedSubmissions })}
                      </p>
                    </div>

                    {/* Points */}
                    <div className="flex-shrink-0 text-right">
                      <div className="flex items-center gap-2 bg-white border border-gray-300 rounded-full px-4 py-2">
                        <span className="text-xl">⭐</span>
                        <span className="text-2xl font-bold text-gray-900">
                          {user.points.toLocaleString()}
                        </span>
                        <span className="text-sm text-gray-600 font-medium">{t('leaderboard.pts')}</span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Footer Info */}
        {leaderboard.length > 0 && (
          <div className="mt-8 text-center text-sm text-gray-500">
            <p>{t('leaderboard.footer.showing', { count: leaderboard.length })}</p>
            <p className="mt-1">{t('leaderboard.footer.update')}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default LeaderboardPage;
